/**
 * A click-to-load third-party player (link previews' inline video: YouTube / Vimeo / Loom, core
 * `Unfurl.videoEmbedUrl`), like Slack's. Until the person presses play it is only the poster (the preview's image,
 * already behind Bundle's image proxy) with a play badge, so nothing loads from the video host before that. The frame
 * is sandboxed (scripts and same-origin for the player itself, presentation for fullscreen, popups so "Watch on
 * YouTube" opens a tab), can't navigate the app, and sends only the origin as referrer (YouTube refuses embeds
 * without one). 16:9 at the given width.
 * iOS/Android: a non-persistent web view.
 */
import { useState } from "react";
import { Icon } from "./Icon.tsx";
import "./embed.css";

export type EmbedFrameProps = {
  src: string;
  /** The frame's accessible title (the video's). */
  title: string;
  /** The play button's label ("Play <title>"). */
  playLabel: string;
  /** The poster image (optional: a dark box without one). */
  poster?: string;
  /** CSS width in px (height follows 16:9). */
  width: number;
  testID?: string;
};

export function EmbedFrame({ src, title, playLabel, poster, width, testID }: EmbedFrameProps) {
  const [playing, setPlaying] = useState(false);
  const height = Math.round((width * 9) / 16);
  if (!playing) {
    return (
      <button type="button" className="b-embed-poster" style={{ width, height }} aria-label={playLabel} onClick={() => setPlaying(true)} data-testid={testID === undefined ? undefined : `${testID}-play`}>
        {poster === undefined ? null : <img src={poster} alt="" referrerPolicy="no-referrer" loading="lazy" />}
        <span className="b-embed-badge" aria-hidden>
          <Icon name="play" size="icon.md" />
        </span>
      </button>
    );
  }
  return (
    <iframe
      className="b-embedframe"
      src={src}
      title={title}
      width={width}
      height={height}
      sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      data-testid={testID}
    />
  );
}
